import { getAuthConfig } from '../../utils/authApi'
import { useMsal } from '@azure/msal-react'
import { InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser'
import axios, { AxiosRequestConfig } from 'axios'
import { useEffect, useState } from 'react'
import { format, isValid } from 'date-fns'
import { t } from 'i18next'
import './OnCallShifts.css'
import type { IOnCall } from '../../interface/IOnCall'
import { IReport } from '../../interface/IReport'
import { useSelector } from 'react-redux'
import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material'
import OnCallShiftsTable from './OnCallShiftsTable'
import React from 'react'

const OnCallShifts = ({ date = null }: { date?: Date | string | null }) => {
    const { instance, accounts } = useMsal()
    const [loading, setLoading] = useState(false)
    const [onCallShifts, setOnCallShifts] = useState<IOnCall | null>(null)
    const [error, setError] = useState<string | null>(null)
    const reportData: IReport[] = useSelector((state: any) => state.report?.value)
    const jidStart = `${process.env.REACT_APP_JID_START}`
    const jidStop = `${process.env.REACT_APP_JID_STOP}`
    const jidBreak = `${process.env.REACT_APP_JID_BREAK}`
    const filteredLines: any[] = []
    const datesWithRealLines = new Set<string>()
    const placeholderAdded = new Set<string>()

    reportData.forEach((item: any) => {
        const lines = Array.isArray(item.calendarLines) ? item.calendarLines : []
        lines.forEach((line: any) => {
            const jid = line.mserp_timeandattendancejobid
            const dateStr: string | undefined = line.mserp_timeprofiledate
            const onCallId = line.mserp_oncalltimeandattendancejobid
            const isFilteredOut = jid === jidStart || jid === jidStop || jid === jidBreak
            if (!isFilteredOut && onCallId) {
                filteredLines.push(line)
                if (dateStr) datesWithRealLines.add(dateStr)
            } else if (isFilteredOut && dateStr && onCallId && onCallId === 'RB') {
                if (!datesWithRealLines.has(dateStr) && !placeholderAdded.has(dateStr)) {
                    filteredLines.push(line)
                    placeholderAdded.add(dateStr)
                }
            }
        })
    })

    const scope: string = process.env.REACT_APP_SCOPE || ''

    const accessTokenRequest: SilentRequest = {
        scopes: [scope],
        account: accounts[0],
        forceRefresh: false,
    }

    const fetchOnCallShifts = async (targetDate: Date | string | null) => {
        if (!targetDate) return
        const d = typeof targetDate === 'string' ? new Date(targetDate) : targetDate
        if (!isValid(d)) {
            setError('Invalid date provided')
            return
        }
        if (!accounts.length) return

        setLoading(true)
        setError(null)
        try {
            const headersConfig = await getAuthConfig(instance, accounts[0])
            const config: AxiosRequestConfig = {
                ...headersConfig,
                params: { year: format(d, 'yyyy') },
            }

            const baseUrl = process.env.REACT_APP_BACKEND_URL
            if (!baseUrl) {
                setLoading(false)
                return
            }

            const response = await axios.get(`${baseUrl}/OnCall`, config)
            setOnCallShifts(response.data ?? [])
        } catch (err: any) {
            if (err instanceof InteractionRequiredAuthError) {
                instance.acquireTokenRedirect(accessTokenRequest)
                return
            }
            console.error('OnCall fetch error', err)
            setError(err.message || 'Failed to load On-Call shifts')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchOnCallShifts(date)
    }, [date, accounts.length])

    return (
        <div>
            <h2>{t('oncall.annual')}</h2>
            {loading && <div>Loading...</div>}
            {error && <div style={{ color: 'red' }}>{error}</div>}
            {!loading && !error && (
                <div className="oncall-container">
                    <div className="oncall-box">
                        <h3>{t('oncall.totaloncallshifts')}</h3>
                        <div className="oncall-value">{onCallShifts?.totalShifts ?? 0}</div>
                    </div>
                    <div className="oncall-box">
                        <h3>{t('oncall.totalweekendoncallshifts')}</h3>
                        <div className="oncall-value">{onCallShifts?.totalWeekendShifts ?? 0}</div>
                    </div>
                    <div className="oncall-box">
                        <h3>{t('oncall.releasedoncallshifts')}</h3>
                        <div className="oncall-value">{onCallShifts?.totalReleasedShifts ?? 0}</div>
                    </div>
                    <div className="oncall-box">
                        <h3>{t('oncall.openoncallshifts')}</h3>
                        <div className="oncall-value">{onCallShifts ? onCallShifts.totalShifts - onCallShifts.totalReleasedShifts : 0}</div>
                    </div>
                </div>
            )}
            <h2>{t('oncall.chosenperiod')}</h2>
            <div>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ padding: 0 }}>{t('report.date')}</TableCell>
                            <TableCell sx={{ padding: 0 }}>{t('oncall.type')}</TableCell>
                            <TableCell sx={{ padding: 0 }}>{t('report.project')}</TableCell>
                            <TableCell sx={{ padding: 0 }}>{t('report.activity')}</TableCell>
                            <TableCell sx={{ padding: 0 }} align="right">
                                {t('report.amount')}
                            </TableCell>
                            <TableCell sx={{ padding: 0 }} align="right">
                                {t('report.from')}
                            </TableCell>
                            <TableCell sx={{ padding: 0 }} align="right">
                                {t('report.to')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>{filteredLines.length > 0 && filteredLines.map((line: any, idx: number) => <OnCallShiftsTable key={idx} line={line} />)}</TableBody>
                </Table>
            </div>
        </div>
    )
}

export default OnCallShifts
